import React from "react";

import { MDXProvider } from "@mdx-js/react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";

import { makeSmartAnchor } from "../../../../../docs/components/SmartAnchor";

import Null from "../../../../../docs/components/Null";
import WithDescriptionPopup, {
  DescriptionPopupProps,
} from "../../../../../docs/components/WithDescriptionPopup";
import WithGeneratedPassword from "../../../../../docs/components/WithGeneratedPassword";

import Popover from "ui/Popover";
import CopyCodeBlock from "ui/CopyCodeBlock";

function DocsMDXProvider({ children }: { children: React.ReactNode }) {
  const SmartAnchor = makeSmartAnchor(Link, true);

  const mdxComponents = {
    img: DocsImage,
    a: SmartAnchor,
    Link: SmartAnchor,
    PublicOnly: Null,
    SignupCTA: Null,
    FeaturePageCTA: Null,
    InAppOnly: React.Fragment,
    // Technically this is not a component, but it allows our existing usage of
    // React.Fragment in our MDX docs to keep working without having to rewrite
    // all those references.
    React: React as any,
    CodeBlock: CopyCodeBlock,
    pre: Pre,
    h1: "h2" as any,
    h2: "h3" as any,
    h3: "h4" as any,
    h4: "h5" as any,
    h5: "h6" as any,
    h6: "span" as any,
  };
  return (
    <WithDescriptionPopup popup={DescriptionPopup}>
      <WithGeneratedPassword>
        <MDXProvider components={mdxComponents}>{children}</MDXProvider>
      </WithGeneratedPassword>
    </WithDescriptionPopup>
  );
}

const DescriptionPopup: React.FunctionComponent<DescriptionPopupProps> = ({
  className,
  info,
}) => {
  return (
    <Popover title="Description" content={info}>
      <FontAwesomeIcon className={className} icon={faQuestionCircle} />
    </Popover>
  );
};

// MDX renders a fenced code block as <pre><code className="language-xxx">…</code></pre>.
// Intercept it (like the public docs site's Pre component) to pull the language
// off the <code> child and hand it to CopyCodeBlock, so fenced blocks get syntax
// highlighting just like an explicit <CodeBlock language="…">.
function Pre({ children, ...rest }: React.ComponentProps<"pre">) {
  if (React.isValidElement(children)) {
    const child = children as React.ReactElement<{
      className?: string;
      children?: React.ReactNode;
    }>;
    const className = child.props.className || "";
    if (child.type === "code" || className.startsWith("language-")) {
      const language = className.replace("language-", "") || "text";
      if (typeof child.props.children === "string") {
        // Drop the fence's trailing newline so the block doesn't render a
        // blank last line (internal newlines are kept).
        const text = child.props.children.replace(/\n$/, "");
        return <CopyCodeBlock language={language}>{text}</CopyCodeBlock>;
      }
    }
  }
  // Fallback for anything that isn't a plain fenced code block.
  return <pre {...rest}>{children}</pre>;
}

type ImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

const DocsImage: React.FunctionComponent<ImgProps> = (props) => {
  return (
    <img
      style={{ maxWidth: "100%", border: "none" }}
      alt={props.alt || "Documentation image"}
      {...props}
    />
  );
};

export default DocsMDXProvider;
